import React from 'react'
import { connect } from 'react-redux'
import PrintIcon from '../icons/Print'
import UserIcon from '../icons/User'
import PrintCateringProductionList from './PrintCateringProductionList'
import OrderNumber from './OrderNumber'
import { getDayOfWeek } from '../helpers/getWeekday'
import { formatVariant } from '../helpers/formatVariant'
import styles from './CateringProductionList.module.css'
import { Dispatch } from '../models'
import { Order } from '../types/Order'
import { GroupedOrder, groupProductionList, ProductionListItem } from '../helpers/groupProductionLists'
import PrintPortal from './PrintPortal'
import Comment from '../icons/Comment'

interface Props {
  orders: Order[]
  date: string,
  storeId: string,
  userId: string,
  onPrintOrder: (payload: { storeId: string, orderIds: string[], userId?: string, isSingleOrder: boolean, type: string }) => void
}

function CateringProductionList({
  orders,
  date,
  storeId,
  userId,
  onPrintOrder
}: Props) {
  const [productionList, setProductionList] = React.useState<ProductionListItem[]>([])
  const [isPrinting, setIsPrinting] = React.useState(false)

  const dayOfWeek = React.useMemo(() => {
    return getDayOfWeek(date)
  }, [date])

  function onPrint() {
    setIsPrinting(true)
    setTimeout(() => {
      window.print()
      setTimeout(() => {
        setIsPrinting(false)
      }, 1000)
    }, 100)
  }

  const printProductionList = () => {
    const idsToPrint = productionList.reduce((acc: string[], list) => {
      const productOrdersIds = list.groupedOrders.map(product =>
        product.orders.map(order => order.orderId)
      ).flat()

      return [...acc, ...productOrdersIds]
    }, [] as string[])
   
    const uniqueIdsToPrint = [...new Set(idsToPrint)]

    onPrintOrder({
      storeId,
      orderIds: uniqueIdsToPrint,
      userId,
      isSingleOrder: false,
      type: 'ProductionList',
    })
    onPrint()
  }

  React.useEffect(() => {
    const groupedOrder = groupProductionList(orders)
    setProductionList(groupedOrder)
  }, [])

  return productionList ? (
    <>
      <div className={styles.sectionHeading}>
        <h2 className={styles.headingText}>
          Leveransdag: {dayOfWeek}, {date}
        </h2>
        <button onClick={printProductionList} className={styles.printButton}>
          <PrintIcon color='#CF2E05' className={styles.printIcon} />
          Skriv ut
        </button>
      </div>

      <div className={styles.productsList}>
        {productionList?.map((product) => {
          if (product.totalAmount > 0) {
            return (
              <div className={styles.productContainer} key={product.productId}>
                <h2 className={styles.productHeading}>
                  {product.productName}{' '}
                  <span className={styles.productTotalAmount}>
                    ({product.totalAmount})
                  </span>
                </h2>
                {product.groupedOrders.map((order) => {
                  return (
                    <div className={styles.variantContainer} key={`${product.productId}-${order.variantId}`}>
                      <div className={styles.variantHeader}>
                        <span className={styles.variantAmount}>{order.totalVariantAmount} st {product.productName}</span>
                        <div className={styles.variantDetails}>
                          {order.variantName &&
                                formatVariant(order.variantName)?.map(
                                  (v: string, index: number) => {
                                    return (
                                      <span
                                        className={styles.variant}
                                        key={order.variantId + index}
                                      >
                                        {v}
                                      </span>
                                    )    
                                  }                       
                                )}
                        </div>
                      </div>
                      <div className={styles.orderList}>
                        {order.orders?.map((o: GroupedOrder, index: number) => {
                          if (o.amount > 0) {
                            return (
                              <div key={`${product.productId}_${order.variantId}_${o.orderNumber}_${index}`}>
                                <div className={styles.orderRow}>
                                  <span className={styles.orderAmount}>
                                    {o.amount} styck
                                  </span>
                                  <span className={styles.orderCustomer}>
                                    <UserIcon
                                      className={styles.userIcon}
                                      color='#CF2E05'
                                    />
                                    <span className={styles.orderCustomerName}>
                                      {o.userName}
                                    </span>
                                  </span>
                                  <span className={styles.orderDelivery}>
                                    {o.deliveryDisplayName}
                                    {o.deliveryTimeSlot ? `: ${o.deliveryTimeSlot}` : ''}
                                  </span>
                                  <span className={styles.orderNumber}>
                                    <OrderNumber
                                      color='#fdc300'
                                      number={o.orderNumber}
                                      dynamicWidth={true}
                                    />
                                  </span>
                                </div>

                                {(o.orderComment || o.productComment || o.storeComment) && (
                                  <div className={styles.commentContainer}>
                                    <Comment className={styles.commentIcon} color='var(--text)'/>
                                    <div className={styles.commentContent}>
                                      {o.productComment && (
                                        <span><span className={styles.commentLabel}>Meddelande från kund:</span> {o.productComment}</span>
                                      )}
                                      {o.orderComment && (
                                        <span><span className={styles.commentLabel}>Övrigt meddelande från kund:</span> {o.orderComment}</span>
                                      )}
                                      {o.storeComment && (
                                        <span><span className={styles.commentLabel}>Intern butikskommentar:</span> {o.storeComment}</span>
                                      )}
                                    </div>
                                  </div> 
                                )}
                              </div>                       
                            )
                          }
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          } else {
            <></>
          }
        })}
      </div>

      {isPrinting && (
        <PrintPortal>
          <div className="printView">
            <PrintCateringProductionList productionList={productionList} date={date} />
          </div>
        </PrintPortal>
      )}

    </>
  ) : (
    <div></div>
  )
}

function mapDispatch(dispatch: Dispatch) {
  return {
    printOrder: dispatch.order.printCateringOrder
  }
}

export default connect(null, mapDispatch)(CateringProductionList)
